import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { mdiCalendarCheckOutline, mdiCarSeat, mdiDoor, mdiCashMultiple, mdiClose } from "@mdi/js";
import Icon from "@mdi/react";

import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import combineObjectsByKey from "utils/combineObjectsByKey";
import getNameInitials from "utils/getNameInitials";
import { StorageBuckets } from "api/storage";
import { GetUserById } from "api/profile";
import useBoundStore from "store";
import { VoteRow, VoteByEventId } from "api/vote";
import compareString from "utils/compareString";
import VirtualizedList from "components/VirtualizedList";
import useNotifier from "hooks/useNotifier";
import RoomManagerApi, { RoomManagerData, EMPTY_ROOM_MANAGER_DATA } from "api/room-manager";
import MoneyManagerApi, { MoneyManagerData, EMPTY_MONEY_MANAGER_DATA } from "api/money-manager";

export interface ParticipantsProps {
  users: GetUserById[];
  votes: VoteByEventId[];
  onBack?: () => void;
}

interface ParticipantDetailsData {
  roomManagerData: RoomManagerData | null;
  moneyManagerData: MoneyManagerData | null;
}

function Participants(props: ParticipantsProps) {
  const { users, votes, onBack } = props;
  const [selectedParticipant, setSelectedParticipant] = useState<(VoteRow & GetUserById) | null>(null);
  const [detailsData, setDetailsData] = useState<ParticipantDetailsData>({
    roomManagerData: null,
    moneyManagerData: null,
  });
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const smallAvatars = useBoundStore((state) => state.miscellaneous.smallAvatars);
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const selected = useBoundStore((state) => state.selected);
  const userAuth = useBoundStore((state) => state.user.auth);
  const { showNotification } = useNotifier();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const roomManagerApi = useMemo(() => new RoomManagerApi(showNotification), [showNotification]);
  const moneyManagerApi = useMemo(() => new MoneyManagerApi(showNotification), [showNotification]);

  // we must use combine because in the groups does not contain the names, avatars of the users
  const combined = (combineObjectsByKey(votes, users, "uid") as Array<VoteRow & GetUserById>).sort((a, b) => {
    // Put auth user first
    if (userAuth && a.uid === userAuth.id) return -1;
    if (userAuth && b.uid === userAuth.id) return 1;
    // Then sort by name
    return compareString(a.name ?? "", b.name ?? "");
  });

  /**
   * On pressing back button close opened dialog
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setSelectedParticipant(null);
    }
  }, [navigationType]);

  /**
   * Fetch room manager and money manager data once for the event
   * (these are event-level data, not participant-specific)
   */
  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!selected.eventId || !selected.groupId) {
        return;
      }

      setIsLoadingDetails(true);
      try {
        const [roomManagerRes, moneyManagerRes] = await Promise.all([
          roomManagerApi.getRoomManagerData(selected.eventId, selected.groupId),
          moneyManagerApi.getMoneyManagerData(selected.eventId, selected.groupId),
        ]);

        setDetailsData({
          roomManagerData: roomManagerRes.data?.data ?? EMPTY_ROOM_MANAGER_DATA,
          moneyManagerData: moneyManagerRes.data?.data ?? EMPTY_MONEY_MANAGER_DATA,
        });
      } catch (error) {
        console.error("Error fetching event details:", error);
        setDetailsData({
          roomManagerData: EMPTY_ROOM_MANAGER_DATA,
          moneyManagerData: EMPTY_MONEY_MANAGER_DATA,
        });
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchEventDetails();
  }, [selected.eventId, selected.groupId, roomManagerApi, moneyManagerApi]);

  /**
   * Handle participant selection - open details dialog
   */
  function handleSelectParticipant(participant: VoteRow & GetUserById): void {
    setSelectedParticipant(participant);
    navigate(location.pathname);
  }

  /**
   * Handle close details dialog
   */
  function handleCloseDetails(): void {
    setSelectedParticipant(null);
    navigate(-1);
  }

  /**
   * Get the room name for the participant
   */
  function getRoomForParticipant(participantUid: number): string | null {
    const room = detailsData.roomManagerData?.rooms.find((r) => r.userIds.includes(participantUid));
    if (!room) return null;

    if (room.zoneId) {
      const zone = detailsData.roomManagerData?.zones.find((z) => z.id === room.zoneId);
      return zone ? `${zone.name} - ${room.name}` : room.name;
    }

    return room.name;
  }

  /**
   * Get which driver the participant is a passenger with
   */
  function getDriverForPassenger(participantUid: number): GetUserById | null {
    const driverVote = votes.find((vote) => vote.passengers.includes(participantUid));
    if (!driverVote) return null;
    return users.find((u) => u.uid === driverVote.uid) ?? null;
  }

  /**
   * Get vehicle seat info for the participant
   */
  function getVehicleSeatInfo(): { type: "driver" | "passenger" | "no_seat"; driverName?: string | null } {
    if (!selectedParticipant) {
      return { type: "no_seat" };
    }

    if (selectedParticipant.vehicle_details) {
      return {
        type: "driver",
        driverName: selectedParticipant.name,
      };
    }

    const driver = getDriverForPassenger(selectedParticipant.uid);
    if (driver) {
      return {
        type: "passenger",
        driverName: driver.name,
      };
    }

    return { type: "no_seat" };
  }

  /**
   * Get money manager info for the participant
   */
  function getMoneyManagerInfo(): {
    amount: string | null;
    paid: boolean;
    currency: string;
  } {
    if (!selectedParticipant || !detailsData.moneyManagerData) {
      return { amount: null, paid: false, currency: "EUR" };
    }

    const entry = detailsData.moneyManagerData.entries[selectedParticipant.uid.toString()];
    if (!entry) {
      return {
        amount: null,
        paid: false,
        currency: detailsData.moneyManagerData.currency ?? "EUR",
      };
    }

    // Calculate per-unit and addon prices (same logic as Summary component)
    const participantIds = combined.map((p) => p.uid.toString());
    const unitPrices: Record<string, number> = {};
    const addonPrices: Record<string, number> = {};

    // Calculate unit prices for per_unit categories
    for (const cat of detailsData.moneyManagerData.categories) {
      if (cat.type === "per_unit") {
        const total = parseFloat(cat.total) || 0;
        const sumUnits = participantIds.reduce(
          (sum, id) => sum + (parseFloat(detailsData.moneyManagerData!.entries[id]?.categories[cat.id] as string) || 0),
          0
        );
        unitPrices[cat.id] = sumUnits > 0 ? total / sumUnits : 0;
      } else if (cat.type === "addon") {
        const total = parseFloat(cat.total) || 0;
        const yesCount = participantIds.filter(
          (id) => !!detailsData.moneyManagerData!.entries[id]?.categories[cat.id]
        ).length;
        addonPrices[cat.id] = yesCount > 0 ? total / yesCount : 0;
      }
    }

    // Calculate individual participant cost
    let totalCost = 0;
    for (const cat of detailsData.moneyManagerData.categories) {
      let cost = 0;
      if (cat.type === "per_unit") {
        cost = (parseFloat(entry.categories[cat.id] as string) || 0) * (unitPrices[cat.id] ?? 0);
      } else {
        cost = entry.categories[cat.id] ? (addonPrices[cat.id] ?? 0) : 0;
      }
      totalCost += cost;
    }

    const advance = parseFloat(entry.advance) || 0;
    const deduction = parseFloat(entry.deduction) || 0;
    const balance = totalCost - deduction - advance;

    return {
      amount: balance > 0 ? balance.toFixed(2) : "0.00",
      paid: entry.paid ?? false,
      currency: detailsData.moneyManagerData.currency ?? "EUR",
    };
  }

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];

    // Render auth user first if present
    const authUser = combined.find((user) => userAuth && user.uid === userAuth.id);
    if (authUser) {
      const hasSeat = votes.find((vote) => vote.passengers.includes(authUser.uid) || authUser.vehicle_details);

      vrList.push(
        <ListItem disablePadding key={authUser.uid}>
          <ListItemButton dense onClick={() => handleSelectParticipant(authUser)}>
            <ListItemAvatar>
              <AsyncAvatar
                avatarFile={authUser.avatar ? smallAvatars[authUser.avatar] : undefined}
                avatarName={authUser.avatar}
                placeholder={getNameInitials(authUser.name)}
                bucket={StorageBuckets.PROFILES_AVATARS}
              />
            </ListItemAvatar>
            <ListItemText primary='You' secondary={`Voted on ${dayjs(authUser.joined_on).format("MMMM DD, HH:mm")}`} />
            <Box color={hasSeat ? "success.main" : "text.secondary"}>
              <Icon aria-label={hasSeat ? "has seat" : "does not have a seat"} path={mdiCarSeat} size={1} />
            </Box>
          </ListItemButton>
        </ListItem>
      );
    }

    // Render other participants
    combined.forEach((user) => {
      // Skip auth user as it's already rendered
      if (userAuth && user.uid === userAuth.id) {
        return;
      }

      const hasSeat = votes.find((vote) => vote.passengers.includes(user.uid) || user.vehicle_details);

      vrList.push(
        <ListItem disablePadding key={user.uid}>
          <ListItemButton dense onClick={() => handleSelectParticipant(user)}>
            <ListItemAvatar>
              <AsyncAvatar
                avatarFile={user.avatar ? smallAvatars[user.avatar] : undefined}
                avatarName={user.avatar}
                placeholder={getNameInitials(user.name)}
                bucket={StorageBuckets.PROFILES_AVATARS}
              />
            </ListItemAvatar>
            <ListItemText
              primary={user.name}
              secondary={`Voted on ${dayjs(user.joined_on).format("MMMM DD, HH:mm")}`}
            />
            <Box color={hasSeat ? "success.main" : "text.secondary"}>
              <Icon aria-label={hasSeat ? "has seat" : "does not have a seat"} path={mdiCarSeat} size={1} />
            </Box>
          </ListItemButton>
        </ListItem>
      );
    });

    return vrList;
  }

  return (
    <PageLayout appBar={<AppBar title='Participants' onBack={onBack} />} paddingBottom={false}>
      {combined.length ? (
        <VirtualizedList
          estimateSize={72}
          rows={generateVirtualizedList()}
          style={{ paddingTop: isMobileDevice ? 0 : "16px" }}
        />
      ) : (
        <DataIconPlaceholder text='Currently, there are no participants in this event' icon={mdiCalendarCheckOutline} />
      )}

      {/* Participant Details Dialog */}
      <Dialog open={selectedParticipant !== null} onClose={handleCloseDetails} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {selectedParticipant?.name}
          <IconButton aria-label='close' onClick={handleCloseDetails} size='small'>
            <Icon path={mdiClose} size={1} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {isLoadingDetails ? (
            <Typography color='text.secondary'>Loading details...</Typography>
          ) : (
            <>
              {/* Vehicle Seat Information */}
              <Box>
                <Box display='flex' alignItems='center' gap={1} mb={1}>
                  <Icon path={mdiCarSeat} size={0.9} />
                  <Typography variant='subtitle2'>Vehicle</Typography>
                </Box>
                {(() => {
                  const seatInfo = getVehicleSeatInfo();
                  if (seatInfo.type === "driver") {
                    return (
                      <Typography variant='body2' color='text.secondary'>
                        Offering a ride
                      </Typography>
                    );
                  }
                  if (seatInfo.type === "passenger") {
                    return (
                      <Typography variant='body2' color='text.secondary'>
                        {seatInfo.driverName}&apos;s car
                      </Typography>
                    );
                  }
                  return (
                    <Typography variant='body2' color='text.secondary'>
                      No seat assigned
                    </Typography>
                  );
                })()}
              </Box>

              {/* Room Assignment */}
              <Box>
                <Box display='flex' alignItems='center' gap={1} mb={1}>
                  <Icon path={mdiDoor} size={0.9} />
                  <Typography variant='subtitle2'>Room</Typography>
                </Box>
                {(() => {
                  const roomName = getRoomForParticipant(selectedParticipant?.uid ?? 0);
                  return roomName ? (
                    <Typography variant='body2' color='text.secondary'>
                      {roomName}
                    </Typography>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Not assigned
                    </Typography>
                  );
                })()}
              </Box>

              {/* Money Manager */}
              <Box>
                <Box display='flex' alignItems='center' gap={1} mb={1}>
                  <Icon path={mdiCashMultiple} size={0.9} />
                  <Typography variant='subtitle2'>Payment</Typography>
                </Box>
                {(() => {
                  const moneyManagerInfo = getMoneyManagerInfo();
                  if (!moneyManagerInfo.amount) {
                    return (
                      <Typography variant='body2' color='text.secondary'>
                        No payment info
                      </Typography>
                    );
                  }
                  return (
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Typography variant='body2' color='text.secondary'>
                        {moneyManagerInfo.amount} {moneyManagerInfo.currency}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          color: moneyManagerInfo.paid ? "success.main" : "warning.main",
                          fontWeight: 600,
                        }}
                      >
                        {moneyManagerInfo.paid ? "Paid" : "Pending"}
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

export default Participants;
